import { useEffect, useState } from "react";
import { ServiceBay, ServiceBaySchedule } from "../models/serviceBay";
import {
  fetchServiceBayList,
  fetchServiceBayScheduleList,
} from "../api/fetchServiceBay";
import {
  createServiceBay,
  createServiceBaySchedule,
  deleteServiceBay,
  deleteServiceBaySchedule,
  updateServiceBay,
  updateServiceBaySchedule,
} from "../api/serviceBay";
import { Occurrence } from "../models/occurrence";
import { fetchOccurenceList } from "../api/fetchOccurence";

function useServiceBay() {
  const [loading, setLoading] = useState<boolean>(false);
  const [serviceBays, setServiceBays] = useState<ServiceBay[]>([]);
  const [serviceBayIdsSelected, setServiceBayIdsSelected] = useState<string[]>(
    []
  );
  const [scheduleServiceBays, setScheduleServiceBays] = useState<
    ServiceBaySchedule[]
  >([]);

  const [currentDate, setCurrentDate] = useState<Date>(new Date());
  const [month, setMonth] = useState<number>(new Date().getMonth());

  const [todaySchedule, setTodaySchedule] = useState<ServiceBaySchedule[]>([]);
  const [tomorrowSchedule, setTomorrowSchedule] = useState<
    ServiceBaySchedule[]
  >([]);

  const [occurrences, setOccurrences] = useState<Occurrence[]>([]);
  const [occurrencePage, setOccurrencePage] = useState<number>(0);
  const [occurrenceLoading, setOccurrenceLoading] = useState<boolean>(false);
  const [occurrenceHasMoreData, setOccurrenceHasMoreData] =
    useState<boolean>(true);

  useEffect(() => {
    getServiceBays();
    getOccurrences(0);
  }, []);

  useEffect(() => {
    if (currentDate.getMonth() !== month) {
      getServiceBaySchedule(
        currentDate,
        serviceBays.map((serviceBay) => serviceBay.id ?? "")
      );
      setMonth(currentDate.getMonth());
    }
  }, [currentDate]);

  const getServiceBays = async () => {
    setLoading(true);
    const data = await fetchServiceBayList();
    if (data) {
      setServiceBays(data);
      getServiceBaySchedule(
        currentDate,
        data.map((serviceBay) => serviceBay.id ?? "")
      );
    }
    setLoading(false);
  };

  const getServiceBaySchedule = async (
    localCurrentDate: Date,
    localServiceBayIds: string[]
  ) => {
    const firstDayOfMonth = new Date(
      localCurrentDate.getFullYear(),
      localCurrentDate.getMonth(),
      1
    );
    const lastDayOfYear = new Date(
      localCurrentDate.getFullYear(),
      11,
      0
    );
    const data = await fetchServiceBayScheduleList(
      firstDayOfMonth,
      lastDayOfYear,
      localServiceBayIds,
      {
        includeOccurrence: true
      }
    );
    if (data) {
      setScheduleServiceBays(data);
      setTodaySchedule(
        await getDateSchedule(
          new Date(),
          data
        )
      );
      setTomorrowSchedule(
        await getDateSchedule(
          new Date(new Date().setDate(new Date().getDate() + 1)),
          data
        )
      );
    }
  };

  const getDateSchedule = async (
    date: Date,
    localServiceBaySchedule: ServiceBaySchedule[]
  ): Promise<ServiceBaySchedule[]> => {
    const data = localServiceBaySchedule.filter((serviceBaySchedule) => {
      if(serviceBaySchedule.startDate?.getMonth() !== date.getMonth() || serviceBaySchedule.startDate?.getFullYear() !== date.getFullYear())
        return false;
      const startDate = serviceBaySchedule.startDate?.getDate();
      const endDate = serviceBaySchedule.endDate?.getDate();
      const currentDate = date.getDate();
      if((startDate && (startDate === currentDate)) || (endDate && (endDate === currentDate)) || (startDate && endDate && (startDate < currentDate && endDate > currentDate)))
        return true;
      return false;
    });
    return data ?? [];
  };

  const save = async (box: ServiceBay) => {
    const { data, error } = await createServiceBay(box);
    if (data) {
      getServiceBays();
    } else {
      throw new Error(error || "Erro ao criar box");
    }
  };

  const update = async (box: ServiceBay) => {
    const data = await updateServiceBay(box);
    if (data) {
      getServiceBays();
    }
  };

  const remove = async (boxId: string) => {
    const { result, error } = await deleteServiceBay(boxId);
    if (result) {
      getServiceBays();
    } else {
      throw new Error(error || "Erro ao deletar box");
    }
  };

  const scheduleServiceBay = async (baySchedule: any) => {
    const { result, error } = await createServiceBaySchedule({
      serviceBay: baySchedule.serviceBay,
      occurrence: baySchedule.occurrence,
      startDate: baySchedule.startDate,
      endDate: baySchedule.endDate,
    });
    if (result) {
      getServiceBaySchedule(
        currentDate,
        serviceBays.map((serviceBay) => serviceBay.id ?? "")
      );
    } else {
      throw new Error(error || "Erro ao criar agendamento");
    }
  };

  const unscheduleServiceBay = async (bayScheduleId: string) => {
    const { result, error } = await deleteServiceBaySchedule(bayScheduleId);
    if (result) {
      getServiceBaySchedule(
        currentDate,
        serviceBays.map((serviceBay) => serviceBay.id ?? "")
      );
    } else {
      throw new Error(error || "Erro ao deletar agendamento");
    }
  };

  const updateSchedule = async (baySchedule: ServiceBaySchedule) => {
    const data = await updateServiceBaySchedule(baySchedule);
    if (data) {
      getServiceBaySchedule(
        currentDate,
        serviceBays.map((serviceBay) => serviceBay.id ?? "")
      );
    }
  };

  const getOccurrences = async (page: number) => {
    setOccurrenceLoading(true);
    const data = await fetchOccurenceList({}, "CONSULTANT", page, 20);
    if (data) {
      setOccurrences((prev) => [...prev, ...data.occurrences]);
      setOccurrenceHasMoreData(data.occurrences.length === 20);
      setOccurrencePage(page + 1);
    }
    setOccurrenceLoading(false);
  };

  return {
    loading,
    currentDate,
    setCurrentDate,
    todaySchedule,
    tomorrowSchedule,
    serviceBays,
    serviceBayIdsSelected,
    setServiceBayIdsSelected,
    scheduleServiceBays,
    getServiceBays,
    getServiceBaySchedule,
    save,
    update,
    remove,
    scheduleServiceBay,
    unscheduleServiceBay,
    updateSchedule,
    occurrences,
    occurrencePage,
    occurrenceLoading,
    occurrenceHasMoreData,
    getOccurrences,
  };
}

export default useServiceBay;
